import React, { FC, useMemo } from "react";

import { ParticipantTypes, TreeNode, useGetArtistTreeQuery } from "@api/graphql";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { Loading } from "@components/loading";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { formatDateStringLong } from "@util/index";

import DetailsRow from "../DetailsRow";

interface CurrentRecipientsProps {
  isArtist?: boolean;
  isCollaboration?: boolean;
  participantId: number | undefined;
}

const getCurrentRecipientsHeader = () => (
  <>
    <h5>Current Recipients</h5>
  </>
);

export const CurrentRecipients: FC<CurrentRecipientsProps> = ({ isArtist, isCollaboration, participantId }) => {
  const { auth } = useAuthenticationContext();
  const { data, loading } = useGetArtistTreeQuery({
    variables: { artistId: participantId ? participantId : 0 },
    skip: !participantId,
  });

  const getFinalBeneficiaries = (nodes: TreeNode[]): TreeNode[] => {
    // Get all node IDs that are parents (have beneficiaries linked)
    const parentNodeIds = new Set(nodes.flatMap((node) => node.parent_nodes.map((parent) => Number(parent))));
    console.log(parentNodeIds);
    // A leaf node = has a parent, but is not a parent itself
    return nodes.filter((node) => {
      const hasParent = node.parent_nodes.length > 0;
      const isNotParent = !parentNodeIds.has(node.node_id);
      const isLeafNode = hasParent && isNotParent;
      console.log(
        `Node ID: ${node.node_id}, Has Parent: ${hasParent}, Is Not Parent: ${isNotParent}, Is Leaf Node: ${isLeafNode}`,
      );
      return isLeafNode;
    });
  };

  const recipients = useMemo(() => {
    const allNodes = data?.getArtistTree?.Nodes ?? [];
    if (isCollaboration) {
      const finalBeneficiaries = getFinalBeneficiaries(allNodes);
      // If no beneficiaries found, return the artist nodes themselves
      const nodesToReturn =
        finalBeneficiaries.length > 0
          ? finalBeneficiaries
          : allNodes.filter(
              (node) =>
                node.attributes.participantType?.includes(ParticipantTypes.ARTIST) ||
                node.attributes.participantType?.includes(ParticipantTypes.COLLABORATION),
            );
      console.log("Collaboration - Final Beneficiaries:", finalBeneficiaries);
      console.log("Collaboration - Nodes to Return:", nodesToReturn);
      return nodesToReturn;
    } else {
      return allNodes;
    }
  }, [data?.getArtistTree?.Nodes, isCollaboration]);

  const shouldDisplayAccordionContent = useMemo(() => {
    if (isArtist || isCollaboration) {
      // Artist No Beneficiaries (internal) => display Artist (participant)
      // Artist with Beneficiary (internal) => display recipients
      // Deceased Artist with immediate beneficiary, 2 recipients (internal) => display recipients
      // So when the participant is an Artist, we display the content
      return true;
    } else {
      // Beneficiary with Artist (internal) => hide content
      // Deceased beneficiary with beneficiaries (internal) => hide content
      return false;
    }
  }, [isArtist, isCollaboration]);

  return (
    <ProtectedComponent
      key={"current_recipient"}
      requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}
      auth={auth}
    >
      {loading && <Loading />}
      <CustomAccordion
        accordionKey={"currentRecipients"}
        contrastOpenStyle={false}
        header={getCurrentRecipientsHeader()}
        defaultActiveKey={"currentRecipients"}
        customHeaderStyle="px-3"
      >
        {shouldDisplayAccordionContent &&
          recipients &&
          participantId &&
          recipients.map(
            (recipient) =>
              recipient.attributes.isRecipientActive && (
                <div className="pb-4" key={recipient.node_id}>
                  <DetailsRow
                    details={[
                      {
                        title: "Recipient",
                        value: (
                          <ParticipantName
                            name={recipient.attributes.companyName || recipient.attributes.name}
                            participantId={recipient.node_id}
                          />
                        ),
                      },
                      {
                        title: "",
                        value: <ParticipantStatusBadge status={recipient.attributes.participantStatus} />,
                        isBadge: true,
                      },
                      {
                        title: "Share",
                        value: `${recipient.attributes.overall_royalty_percentage}%`,
                      },
                      {
                        title: "Start date",
                        value: formatDateStringLong(recipient.attributes.start_date) || "-",
                      },
                    ]}
                    className="mt-0"
                  />
                </div>
              ),
          )}
      </CustomAccordion>
    </ProtectedComponent>
  );
};
